/**
 * WizardSuccessScreen  (default export)
 *
 * Full-panel success screen shown after a bulk operation completes.
 * Two variants cover the two success states used across the platform:
 *
 *   variant="tc"    — test-case bulk save success (JiraReviewScreen)
 *   variant="story" — Jira story creation success with next-action choice
 *                     (DocumentToJiraWizard)
 *
 * Props — variant="tc"
 * ─────────────────────
 * successCount  number   – Number of successfully created TCs
 * storyKey      string   – Optional Jira story key that was linked
 * linkError     bool     – Whether the Jira linking step failed
 *
 * Props — variant="story"
 * ────────────────────────
 * createdItems  array    – [{ key, title }] list of created Jira stories
 * subtitle      string   – Secondary line under the heading
 * onChoice      fn       – (choice: 'continue' | 'exit') => void
 * continueLabel string   – Label for the primary action card
 * continueDesc  string   – Description for the primary action card
 * exitLabel     string   – Label for the secondary action card
 * exitDesc      string   – Description for the secondary action card
 *
 *
 * WizardSaveResultBanner  (named export)
 *
 * Success / Partial / Error result banner rendered above a review list after
 * a bulk-save attempt. Returns null when savePhase is not a terminal state.
 *
 * Props
 * ─────
 * savePhase   string  – One of SAVE_PHASE constants
 * saveResult  object  – { successCount, failedCount, linkError, storyKey }
 * SAVE_PHASE  object  – The SAVE_PHASE constants object
 *
 * Usage
 * ─────
 * import WizardSuccessScreen, { WizardSaveResultBanner } from './WizardSuccessScreen';
 */

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import LinkIcon from '@mui/icons-material/Link';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Chip, CircularProgress, Divider, Typography } from '@mui/material';


const TcSuccessContent = ({ successCount, storyKey, linkError }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 8,
      gap: 2,
      minHeight: 260,
    }}
  >
    <Box
      sx={{
        width: 72,
        height: 72,
        borderRadius: '50%',
        bgcolor: '#F0FDF4',
        border: '2px solid',
        borderColor: 'success.light',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CheckCircleIcon sx={{ fontSize: 36, color: 'success.main' }} />
    </Box>

    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="subtitle1" fontWeight={600} color="success.dark">
        {successCount} test case{successCount !== 1 ? 's' : ''} created
      </Typography>
      {storyKey && !linkError && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}
        >
          <LinkIcon sx={{ fontSize: 15 }} />
          Linked to {storyKey}
        </Typography>
      )}
      {linkError && (
        <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.5 }}>
          Jira linking failed — link manually from traceability tab
        </Typography>
      )}
    </Box>

    <CircularProgress size={16} thickness={3} sx={{ color: 'success.main', mt: 1 }} />
  </Box>
);

const ActionCard = ({ icon, label, description, primary, onClick }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick?.()}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      p: 2,
      border: primary ? '2px solid' : '1.5px solid',
      borderColor: primary ? 'primary.main' : 'divider',
      borderRadius: 2,
      cursor: 'pointer',
      bgcolor: primary ? 'primary.50' : 'transparent',
      outline: 'none',
      transition: 'box-shadow 0.15s',
      '&:hover': { boxShadow: primary ? 2 : 1, bgcolor: primary ? 'primary.50' : 'grey.50' },
      '&:focus-visible': { boxShadow: '0 0 0 3px #3b82f6' },
    }}
  >
    {icon}
    <Box>
      <Typography variant="body2" fontWeight={primary ? 700 : 600} color={primary ? 'primary.main' : 'text.primary'}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {description}
      </Typography>
    </Box>
  </Box>
);

const StorySuccessContent = ({
  createdItems = [],
  subtitle,
  onChoice,
  continueLabel = 'Continue',
  continueDesc,
  exitLabel = 'Exit',
  exitDesc,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 2 }}>
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        bgcolor: 'success.light',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CheckCircleOutlineIcon sx={{ fontSize: 32, color: 'success.main' }} />
    </Box>

    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h6" fontWeight={700}>
        {createdItems.length} Jira {createdItems.length === 1 ? 'story' : 'stories'} created successfully
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>

    {createdItems.length > 0 && (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
        {createdItems.map((item) => {
          const isClickable = !!item.url;

          return (
            <Chip
              key={item.key}
              label={item.key}
              {...(isClickable
                ? {
                  component: 'a',
                  href: item.url,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  clickable: true,
                  onClick: (e) => e.stopPropagation(),
                }
                : {})}
              size="large"
              sx={{
                fontWeight: 600,
                bgcolor: 'primary.50',
                color: 'primary.main',
                border: '1px solid',
                borderColor: 'primary.200',
                textDecoration: 'none',
                cursor: isClickable ? 'pointer' : 'default',
                '&:hover': isClickable
                  ? {
                    bgcolor: 'primary.100',
                  }
                  : {},
              }}
            />
          );
        })}
      </Box>
    )}

    <Divider sx={{ width: '100%' }} />

    <Box sx={{ width: '100%' }}>
      <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ mb: 1.5 }}>
        What would you like to do next?
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <ActionCard
          primary
          icon={<AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: 22 }} />}
          label={continueLabel}
          description={continueDesc}
          onClick={() => onChoice?.('continue')}
        />
        <ActionCard
          icon={<ExitToAppIcon sx={{ color: 'text.secondary', fontSize: 22 }} />}
          label={exitLabel}
          description={exitDesc}
          onClick={() => onChoice?.('exit')}
        />
      </Box>
    </Box>
  </Box>
);


export const WizardSaveResultBanner = ({ savePhase, saveResult, SAVE_PHASE }) => {
  if (
    savePhase !== SAVE_PHASE.SUCCESS &&
    savePhase !== SAVE_PHASE.PARTIAL &&
    savePhase !== SAVE_PHASE.ERROR
  ) {
    return null;
  }

  const isSuccess = savePhase === SAVE_PHASE.SUCCESS;
  const isPartial = savePhase === SAVE_PHASE.PARTIAL;
  const isError = savePhase === SAVE_PHASE.ERROR;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 2,
        mb: 2,
        borderRadius: 1.5,
        border: '1.5px solid',
        borderColor: isSuccess ? 'success.light' : isPartial ? 'warning.light' : 'error.light',
        bgcolor: isSuccess ? '#F0FDF4' : isPartial ? '#FFFBEB' : '#FEF2F2',
      }}
    >
      {isSuccess && <CheckCircleIcon sx={{ fontSize: 22, color: 'success.main', flexShrink: 0, mt: 0.1 }} />}
      {isPartial && <WarningAmberIcon sx={{ fontSize: 22, color: 'warning.main', flexShrink: 0, mt: 0.1 }} />}
      {isError && <ErrorOutlineIcon sx={{ fontSize: 22, color: 'error.main', flexShrink: 0, mt: 0.1 }} />}

      <Box>
        <Typography
          variant="body2"
          fontWeight={600}
          color={isSuccess ? 'success.dark' : isPartial ? 'warning.dark' : 'error.dark'}
        >
          {isSuccess && `${saveResult.successCount} test case${saveResult.successCount !== 1 ? 's' : ''} created successfully`}
          {isPartial && `${saveResult.successCount} saved, ${saveResult.failedCount} failed`}
          {isError && 'All test cases failed to save'}
        </Typography>

        {isSuccess && saveResult.storyKey && !saveResult.linkError && (
          <Typography
            variant="caption"
            color="success.dark"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}
          >
            <LinkIcon sx={{ fontSize: 13 }} />
            Linked to {saveResult.storyKey} · Closing in a moment…
          </Typography>
        )}
        {isSuccess && saveResult.linkError && (
          <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.25 }}>
            Note: Jira linking failed. Link manually from the traceability tab.
          </Typography>
        )}
        {isPartial && (
          <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.25 }}>
            Failed cards are highlighted below. Use Retry Failed to attempt them again.
            {saveResult.linkError && ' Jira linking also failed for saved test cases.'}
          </Typography>
        )}
        {isError && (
          <Typography variant="caption" color="error.dark" sx={{ display: 'block', mt: 0.25 }}>
            Please check your connection and try again.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const WizardSuccessScreen = ({ variant = 'tc', ...props }) => {
  if (variant === 'story') return <StorySuccessContent {...props} />;
  return <TcSuccessContent {...props} />;
};

export default WizardSuccessScreen;